import axios from 'axios';
import { API_URL } from '../utils/constant';

class RiskAssignmentService {
    constructor() {
        this.baseURL = API_URL;
    }

    // Get authentication headers
    getHeaders() {
        const token = localStorage.getItem('session');
        return {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
        };
    }

    // Make API request with error handling
    async makeRequest(endpoint, options = {}) {
        const url = `${this.baseURL}${endpoint}`;
        const config = {
            headers: this.getHeaders(),
            ...options
        };

        try {
            const response = await axios({
                url,
                method: config.method || 'GET',
                data: config.data,
                headers: config.headers
            });
            return response.data;
        } catch (error) {
            // console.error removed
            throw error;
        }
    }

    // ==================== RISK MANAGEMENT APIs ====================

    /**
     * Get all risks with assignments
     * @param {string} organisationId - Organization ID
     * @param {Object} filters - Filter parameters
     * @returns {Promise<Object>} Risk data with pagination
     */
    async getRisks(organisationId, filters = {}) {
        const params = new URLSearchParams();

    //

        // Extract categoryV from filters for URL path only (not query)
        let categoryV = (filters.categoryV || 'all').toString();
        // Normalize a few common variants to backend-expected slugs
        const normalize = (v) => {
            const map = {
                all: 'all',
                'risk-accepted': 'risk-accepted',
                'false-positive': 'false-positive',
                remediated: 'remediated',
                remidiated: 'remediated' // handle misspelling from UI
            };
            return map[v] || v;
        };
        categoryV = normalize(categoryV);
    //

        // Optionally fallback to first category (kept for backward compatibility)
        if (!categoryV && filters.category && filters.category.length > 0) {
            categoryV = filters.category[0];
            //
        }

        // Build query params excluding categoryV
        Object.keys(filters).forEach(key => {
            if (key === 'categoryV') return; // Do not include in query string
            const value = filters[key];
            
            // Handle special case for severity to ensure proper formatting
            if (key === 'severity' && value !== undefined && value !== '') {
                params.append('severity', value);
            }
            else if (value !== undefined && value !== '') {
                if (Array.isArray(value)) {
                    if (value.length > 0) {
                        value.forEach(v => params.append(key, v));
                    }
                } else {
                    params.append(key, value);
                }
            }
        });

    const query = params.toString();
    // Send categoryV after organisationId in the path and not as query param
    const finalUrl = `/risks/${categoryV || 'all'}/${organisationId}${query ? `?${query}` : ''}`;

        return this.makeRequest(finalUrl);
    }

    /**
     * Get risk summary
     * @param {string} organisationId - Organization ID
     * @param {boolean} force - Force refresh summary
     * @returns {Promise<Object>} Risk summary data
     */
    async getRiskSummary(organisationId, force = false) {
        const params = force ? '?force=true' : '';
        return this.makeRequest(`/risks/summary/${organisationId}${params}`);
    }

    /**
     * Update risk status
     * @param {string} riskId - Risk ID
     * @param {string} status - New status
     * @returns {Promise<Object>} Update response
     */
    async updateRiskStatus(riskId, status) {
        return this.makeRequest(`/risks/${riskId}/status`, {
            method: 'PATCH',
            data: { status }
        });
    }

    /**
     * Update risk severity
     * @param {string} riskId - Risk ID
     * @param {string} severity - New severity
     * @returns {Promise<Object>} Update response
     */
    async updateRiskSeverity(riskId, severity) {
        return this.makeRequest(`/risks/${riskId}/severity`, {
            method: 'PATCH',
            data: { severity }
        });
    }

    // ==================== ASSIGNMENT MANAGEMENT APIs ====================

    /**
     * Assign risk to users
     * @param {string} organisationId - Organization ID
     * @param {Object} assignmentData - Assignment data
     * @returns {Promise<Object>} Assignment response
     */
    async assignRisk(organisationId, assignmentData) {
        // console.log removed
        return this.makeRequest(`/risks/assign/${organisationId}`, {
            method: 'POST',
            data: assignmentData
        });
    }

    /**
     * Get assignment for specific risk
     * @param {string} organisationId - Organization ID
     * @param {string} riskId - Risk ID
     * @param {string} collectionName - Collection name
     * @returns {Promise<Object>} Assignment data
     */
    async getAssignment(organisationId, riskId, collectionName) {
        return this.makeRequest(`/risks/assignment/${organisationId}/${riskId}/${collectionName}`);
    }

    /**
     * Update assignment
     * @param {string} assignmentId - Assignment ID
     * @param {Object} updateData - Update data
     * @returns {Promise<Object>} Update response
     */
    async updateAssignment(assignmentId, updateData) {
        return this.makeRequest(`/risks/assignment/${assignmentId}`, {
            method: 'PUT',
            data: updateData
        });
    }

    /**
     * Get all assignments
     * @param {string} organisationId - Organization ID
     * @param {Object} filters - Filter parameters
     * @returns {Promise<Object>} Assignments data with pagination
     */
    async getAssignments(organisationId, filters = {}) {
        const params = new URLSearchParams();
        Object.keys(filters).forEach(key => {
            if (filters[key] !== undefined && filters[key] !== '') {
                params.append(key, filters[key]);
            }
        });

        return this.makeRequest(`/risks/assignment/${organisationId}?${params.toString()}`);
    }

    /**
     * Remove assignment
     * @param {string} assignmentId - Assignment ID
     * @returns {Promise<Object>} Remove response
     */
    async removeAssignment(assignmentId) {
        return this.makeRequest(`/risks/assignment/${assignmentId}`, {
            method: 'DELETE'
        });
    }

    // ==================== USER MANAGEMENT APIs ====================

    /**
     * Get users for assignment
     * @param {string} organisationId - Organization ID
     * @param {string} search - Search query
     * @returns {Promise<Object>} Users data
     */
    async getUsers(organisationId, search = '') {
        const params = search ? `&search=${encodeURIComponent(search)}` : '';
        return this.makeRequest(`/user?id=${organisationId}${params}`);
    }

    // ==================== HELPER FUNCTIONS ====================

    /**
     * Get collection name from category
     * @param {string} category - Risk category
     * @returns {string} Collection name
     */
    getCollectionName(category) {
        const collectionMap = {
            // Active Vulnerabilities
            activeVulnerabilities: 'active_vulns_records',
            // Application Security (Domain Vulnerabilities)
            applicationSecurity: 'nuclei_domain_vulnerability',
            // Passive Vulnerability Assessment
            passiveVulnerabilities: 'delta_cve_detail',
            // Vulnerable Web Technologies
            vulnerableTechnologies: 'subdomaintechstack',
            // Email Security - SPF/DMARC Records
            emailSecurity: 'dnsdetail',
            // Expired & Vulnerable SSL Certificates
            sslCertificates: 'ssldetail',
            // Breached Credentials
            leakedCredentials: 'deltaleakedcredential',
            // Malware Infected Computers
            malwareCredentials: 'deltamalwarecredential',
            // Leaked Sessions
            leakedSessions: 'deltaleakedsession',
            // Source Code Leakage
            leakedCode: 'deltaleakedcode',
            // Pastebin Monitoring
            pastebinLeaks: 'deltapastebinresult',
            // Personal Information Leakage
            personalInfo: 'deltapersonalinformation',
            // Telegram Monitoring
            telegramMentions: 'telegramforum',
            // API Documentation Leak Monitoring
            postmanExposure: 'deltapostmandetail',
            // Open Cloud Buckets Check
            cloudBuckets: 'deltacloudbucketdetail',
            // Dark Web Monitoring
            darkWebForums: 'darkwebforum',
            // Look Alike Domain Check
            lookAlikeDomains: 'delta-domain_squattings',
            // Fake Mobile Applications Check
            mobileApps: 'deltamobileapp',
            // Fake Social Media Profiles Check
            socialMedia: 'delta_social_media_profile',
            // Brand Mentions
            brandMentions: 'brand_mentions'
        };
        return collectionMap[category] || category;
    }

    /**
     * Format severity for display
     * @param {string} severity - Severity level
     * @returns {Object} Formatted severity with color
     */
    formatSeverity(severity) {
        const severityColors = {
            'Low': '#28a745',
            'Medium': '#ffc107',
            'High': '#fd7e14',
            'Critical': '#dc3545'
        };
        return {
            label: severity,
            color: severityColors[severity] || '#6c757d'
        };
    }

    /**
     * Format assignment status
     * @param {string} status - Assignment status
     * @returns {Object} Formatted status with color and icon
     */
    formatAssignmentStatus(status) {
        const statusConfig = {
            'pending': { label: 'Pending', color: '#ffc107', icon: '⏳' },
            'in-progress': { label: 'In Progress', color: '#17a2b8', icon: '🔄' },
            'completed': { label: 'Completed', color: '#28a745', icon: '✅' },
            'rejected': { label: 'Rejected', color: '#dc3545', icon: '❌' }
        };
        return statusConfig[status] || { label: status, color: '#6c757d', icon: '❓' };
    }

    /**
     * Handle API errors
     * @param {Error} error - Error object
     * @returns {void}
     */
    handleApiError(error) {
        if (error.response?.status === 401) {
            // Redirect to login
            window.location.href = '/login';
        } else if (error.response?.status === 403) {
            // Show permission error
            // console.error removed
        } else if (error.response?.status === 404) {
            // Show not found error
            // console.error removed
        } else {
            // Show generic error
            // console.error removed
        }
    }

    /**
     * Create assignment data object
     * @param {string} riskId - Risk ID
     * @param {string} category - Risk category
     * @param {Array} assignedTo - Array of user IDs
     * @param {string} notes - Assignment notes
     * @param {string} dueDate - Due date (ISO string)
     * @param {string} status - Assignment status
     * @returns {Object} Assignment data object
     */
    createAssignmentData(riskId, category, assignedTo, notes = '', dueDate = null, status = 'pending') {
        return {
            riskId,
            category,
            assignedTo,
            notes,
            dueDate,
            status
        };
    }

    /**
     * Perform risk action (remediate, false positive, accept risk, reset)
     * @param {string} organisationId - Organization ID
     * @param {Object} actionData - Action data
     * @returns {Promise<Object>} Action response
     */
    async performRiskAction(organisationId, actionData) {
        // console.log removed

        // Validate required parameters
        const { action, category } = actionData;
        if (!action || !category) {
            // console.error removed
            throw new Error('Missing required parameters: action, category');
        }

        // Find the category-specific parameter name
        const categoryParamMap = {
            'activeVulnerabilities': 'pvaIds',
            'passiveVulnerabilities': 'pvaIds',
            'applicationSecurity': 'ids',
            'vulnerableTechnologies': 'techIds',
            'emailSecurity': 'spfDmarcIds',
            'sslCertificates': 'subDomainIds',
            'leakedCredentials': 'leakedCredentialsIds',
            'malwareCredentials': 'malwareCredentialsIds',
            'leakedSessions': 'leakedSessionIds',
            'leakedCode': 'leakedCodeIds',
            'pastebinLeaks': 'pastebinIds',
            'personalInfo': 'piiIds',
            'telegramMentions': 'telegramIds',
            'postmanExposure': 'postmanIds',
            'cloudBuckets': 'cloudBucketIds',
            'darkWebForums': 'darkWebForumIds',
            'lookAlikeDomains': 'lookalikeDomainIds',
            'mobileApps': 'lookalikeAppIds',
            'socialMedia': 'fakeSocialMediaIds',
            'brandMentions': 'brandMentionIds'
        };

        const categoryParamName = categoryParamMap[category] || 'riskIds';
        const riskIds = actionData[categoryParamName] || actionData.riskIds;

        if (!riskIds) {
            // console.error removed
            throw new Error(`Missing required parameter: ${categoryParamName} or riskIds`);
        }

        // Ensure riskIds is an array and include both parameter formats
        const validatedActionData = {
            ...actionData,
            riskIds: Array.isArray(riskIds) ? riskIds : [riskIds],
            [categoryParamName]: Array.isArray(riskIds) ? riskIds : [riskIds]
        };


        return this.makeRequest(`/risks/action/${organisationId}`, {
            method: 'POST',
            data: validatedActionData
        });
    }
}

export default RiskAssignmentService; 